import { memo, useState, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Music, X, Upload, CheckCircle, Play, Pause, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import { uploadToR2 } from '../../utils/uploadToR2';
import { describeUploadError } from '../../utils/uploadLimits';
import { toast } from 'sonner';

const UploadAudioNode = memo(({ id, data, selected }) => {
  const [dragActive, setDragActive] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);
  const addAsset = useWorkflowStore((state) => state.addAsset);

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.type.includes('audio')) {
      toast.error('Formato no admitido. Usá MP3, WAV, M4A o AAC.', { position: 'bottom-right' });
      return;
    }

    const localPreview = URL.createObjectURL(file);
    updateNodeData(id, {
      file,
      preview: localPreview,
      fileName: file.name,
      hasOutput: false,
      outputUrl: null,
      outputType: 'audio',
      uploading: true,
      uploadError: null,
    });

    setUploadProgress(0);
    uploadToR2(file, { onProgress: setUploadProgress })
      .then(({ url, r2Key, filename }) => {
        try { URL.revokeObjectURL(localPreview); } catch { /* ignore */ }
        updateNodeData(id, {
          preview: url,
          hasOutput: true,
          outputUrl: url,
          r2Key,
          uploading: false,
          uploaded: true,
          uploadError: null,
        });
        addAsset?.({
          id: `asset_${Date.now()}`,
          type: 'audio',
          tool: 'upload',
          filename: filename || file.name,
          url,
          r2Key,
          createdAt: new Date().toISOString(),
        });
      })
      .catch((err) => {
        console.error('R2 upload failed (audio):', err);
        const message = describeUploadError(err);
        toast.error(message, { position: 'bottom-right', duration: 8000 });
        updateNodeData(id, { uploading: false, uploadError: message });
      })
      .finally(() => setUploadProgress(null));
  }, [id, updateNodeData, addAsset]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragActive(false);
  }, []);

  const clearAudio = useCallback(() => {
    if (data.preview && data.preview.startsWith('blob:')) {
      try { URL.revokeObjectURL(data.preview); } catch { /* ignore */ }
    }
    updateNodeData(id, {
      file: null,
      preview: null,
      fileName: null,
      hasOutput: false,
      outputUrl: null,
      outputType: null,
      r2Key: null,
      uploading: false,
      uploaded: false,
      uploadError: null,
    });
    setUploadProgress(null);
  }, [id, data.preview, updateNodeData]);

  const handleSourcePick = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        handleFile(result.file);
      } else if (result.url) {
        updateNodeData(id, {
          file: null,
          preview: result.url,
          fileName: result.fileName,
          hasOutput: true,
          outputUrl: result.url,
          outputType: 'audio',
          fromGallery: true,
          uploadError: null,
        });
      }
    });
    openSourcePicker(id, ['audio'], 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/mpeg,audio/ogg');
  }, [id, openSourcePicker, handleFile, updateNodeData]);

  const togglePlay = useCallback(() => {
    const audio = document.getElementById(`audio-${id}`);
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [id, isPlaying]);

  return (
    <div className={`node-container upload-audio-node ${selected ? 'selected' : ''}`} data-testid="upload-audio-node">
      {/* Header */}
      <div className="node-header upload-audio-header">
        <div className="node-header-left">
          <Music size={16} />
          <span>Subir Audio</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content">
        {data.preview ? (
          <div className="upload-audio-preview">
            <audio id={`audio-${id}`} src={data.preview} onEnded={() => setIsPlaying(false)} />
            <button className="upload-audio-play-btn" onClick={togglePlay}>
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <div className="upload-audio-details">
              <span className="upload-audio-name">{data.fileName?.slice(0, 18)}...</span>
              <div className="upload-audio-badge">
                {data.uploading ? (
                  <>
                    <Loader2 size={10} className="spin-icon" />
                    <span>{uploadProgress != null ? `${uploadProgress}%` : 'Subiendo...'}</span>
                  </>
                ) : data.uploadError ? (
                  <span className="upload-error-line" role="alert" title={data.uploadError} data-testid="upload-audio-error">
                    <AlertTriangle size={10} />
                    <span>No se pudo subir</span>
                    {data.file && (
                      <button type="button" className="upload-retry-btn nodrag" onClick={() => handleFile(data.file)}>
                        <RotateCcw size={10} /> Reintentar
                      </button>
                    )}
                  </span>
                ) : (
                  <>
                    <CheckCircle size={10} />
                    <span>Listo</span>
                  </>
                )}
              </div>
            </div>
            <button className="upload-clear-btn audio" onClick={clearAudio}>
              <X size={12} />
            </button>
          </div>
        ) : (
          <div
            className={`upload-audio-dropzone ${dragActive ? 'active' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Music size={24} className="upload-audio-icon" />
            <span className="upload-audio-text">Arrastra un audio o</span>
            <span className="upload-audio-hint">MP3, WAV, M4A, AAC</span>
            <button type="button" className="node-load-btn" onClick={handleSourcePick} data-testid="load-btn-upload-audio">
              <Upload size={14} /> Cargar
            </button>
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

UploadAudioNode.displayName = 'UploadAudioNode';

export default UploadAudioNode;
